import React from 'react';
import { Tabs } from 'antd';
import PageTitle from '../../../Utils/PageTitle';
import { StyledTabs } from '../UsersPage/styledComponents';
import NTATab from './NTATab';
import { createActiveDxGroupsColumns } from './columnConfigs/activeDxGroups';
import { createIcd10ToActiveDxColumns } from './columnConfigs/icd10ToActiveDx';
import { createNtaComorbiditiesI8000Columns } from './columnConfigs/ntaComorbiditiesI8000';
import { createIcd10ToI8000Columns } from './columnConfigs/icd10ToI8000';
import { createIcd10MaxNtaLookupColumns } from './columnConfigs/icd10MaxNtaLookup';
import {
  getActiveDxGroups,
  createActiveDxGroup,
  updateActiveDxGroup,
  getComorbidities,
  createComorbidity,
  updateComorbidity,
  getIcd10ToActiveDx,
  createIcd10ToActiveDx,
  updateIcd10ToActiveDx,
  getIcd10ToI8000,
  createIcd10ToI8000,
  updateIcd10ToI8000,
  getIcd10MaxLookup,
  createIcd10MaxLookup,
  updateIcd10MaxLookup
} from '../../../Apis/NTA';

const { TabPane } = Tabs;

const ACTIVE_DX_GROUPS_FIELDS = [
  { key: 'groupCode', label: 'Group Code', type: 'text' },
  { key: 'displayName', label: 'Display Name', type: 'text' },
  { key: 'points', label: 'Points', type: 'number' },
  { key: 'mdsSourceItem', label: 'MDS Source Item', type: 'text' },
  { key: 'sourceUrl', label: 'Source URL', type: 'text' }
];

const ICD10_TO_ACTIVE_DX_FIELDS = [
  { key: 'groupCode', label: 'Group Code', type: 'text' },
  { key: 'matchType', label: 'Match Type', type: 'text' },
  { key: 'pattern', label: 'Pattern', type: 'text' },
  { key: 'priority', label: 'Priority', type: 'number' },
  { key: 'description', label: 'Description', type: 'text' },
  { key: 'sourceUrl', label: 'Source URL', type: 'text' }
];

const NTA_COMORBIDITIES_I8000_FIELDS = [
  { key: 'comorbidityCode', label: 'Comorbidity Code', type: 'text' },
  { key: 'displayName', label: 'Display Name', type: 'text' },
  { key: 'points', label: 'Points', type: 'number' },
  { key: 'sourceType', label: 'Source Type', type: 'text' },
  { key: 'sourceUrl', label: 'Source URL', type: 'text' }
];

const ICD10_TO_I8000_FIELDS = [
  { key: 'icd10Undotted', label: 'ICD10 Undotted', type: 'text' },
  { key: 'icd10', label: 'ICD10', type: 'text' },
  { key: 'shortDescription', label: 'Short Description', type: 'text' },
  { key: 'longDescription', label: 'Long Description', type: 'textarea' },
  { key: 'comorbidityDisplayName', label: 'Comorbidity Display Name', type: 'text' },
  { key: 'points', label: 'Points', type: 'number' },
  { key: 'sourceUrlPoints', label: 'Source URL (Points)', type: 'text' },
  { key: 'sourceUrlMapping', label: 'Source URL (Mapping)', type: 'text' }
];

const ICD10_MAX_NTA_LOOKUP_FIELDS = [
  { key: 'icd10Undotted', label: 'ICD10 Undotted', type: 'text' },
  { key: 'icd10', label: 'ICD10', type: 'text' },
  { key: 'shortDescription', label: 'Short Description', type: 'text' },
  { key: 'longDescription', label: 'Long Description', type: 'textarea' },
  { key: 'activeDxGroupCode', label: 'Active Dx Group Code', type: 'text' },
  { key: 'activeDxGroupName', label: 'Active Dx Group Name', type: 'text' },
  { key: 'activeDxPoints', label: 'Active Dx Points', type: 'number' },
  { key: 'exactMappedCategories', label: 'Exact Mapped Categories', type: 'textarea' },
  { key: 'exactMappedPoints', label: 'Exact Mapped Points', type: 'number' },
  { key: 'maxTotalPointsAssumingActive', label: 'Max Total Points (Active)', type: 'number' },
  { key: 'sourceUrlPoints', label: 'Source URL (Points)', type: 'text' },
  { key: 'sourceUrlMapping', label: 'Source URL (Mapping)', type: 'text' }
];

const ACTIVE_DX_GROUPS_API = {
  fetchAll: getActiveDxGroups,
  create: createActiveDxGroup,
  update: updateActiveDxGroup
};

const ICD10_TO_ACTIVE_DX_API = {
  fetchAll: getIcd10ToActiveDx,
  create: createIcd10ToActiveDx,
  update: updateIcd10ToActiveDx
};

const COMORBIDITIES_API = {
  fetchAll: getComorbidities,
  create: createComorbidity,
  update: updateComorbidity
};

const ICD10_TO_I8000_API = {
  fetchAll: getIcd10ToI8000,
  create: createIcd10ToI8000,
  update: updateIcd10ToI8000
};

const ICD10_MAX_NTA_LOOKUP_API = {
  fetchAll: getIcd10MaxLookup,
  create: createIcd10MaxLookup,
  update: updateIcd10MaxLookup
};

const NTAPage = () => {
  return (
    <>
      <PageTitle title='NTA Management' />
      <StyledTabs defaultActiveKey='activeDxGroups' type='card'>
        <TabPane key='activeDxGroups' tab='Active Dx Groups'>
          <NTATab
            createColumns={createActiveDxGroupsColumns}
            storageKey='nta_active_dx_groups'
            tabLabel='Active Dx Group'
            formFields={ACTIVE_DX_GROUPS_FIELDS}
            apiHandlers={ACTIVE_DX_GROUPS_API}
            pageName='NTA_ACTIVE_DX_GROUPS'
          />
        </TabPane>

        <TabPane key='icd10ToActiveDx' tab='ICD10 to Active Dx'>
          <NTATab
            createColumns={createIcd10ToActiveDxColumns}
            storageKey='nta_icd10_to_active_dx'
            tabLabel='ICD10 to Active Dx'
            formFields={ICD10_TO_ACTIVE_DX_FIELDS}
            apiHandlers={ICD10_TO_ACTIVE_DX_API}
            pageName='NTA_ICD10_TO_ACTIVE_DX'
          />
        </TabPane>

        <TabPane key='ntaComorbiditiesI8000' tab='NTA Comorbidities I8000'>
          <NTATab
            createColumns={createNtaComorbiditiesI8000Columns}
            storageKey='nta_comorbidities_i8000'
            tabLabel='NTA Comorbidities I8000'
            formFields={NTA_COMORBIDITIES_I8000_FIELDS}
            apiHandlers={COMORBIDITIES_API}
            pageName='NTA_NTA_COMORBIDITIES_I8000'
          />
        </TabPane>

        <TabPane key='icd10ToI8000' tab='ICD10 to I8000'>
          <NTATab
            createColumns={createIcd10ToI8000Columns}
            storageKey='nta_icd10_to_i8000'
            tabLabel='ICD10 to I8000'
            formFields={ICD10_TO_I8000_FIELDS}
            apiHandlers={ICD10_TO_I8000_API}
            pageName='NTA_ICD10_TO_I8000'
          />
        </TabPane>

        <TabPane key='icd10MaxNtaLookup' tab='ICD10 Max NTA Lookup'>
          <NTATab
            createColumns={createIcd10MaxNtaLookupColumns}
            storageKey='nta_icd10_max_nta_lookup'
            tabLabel='ICD10 Max NTA Lookup'
            formFields={ICD10_MAX_NTA_LOOKUP_FIELDS}
            apiHandlers={ICD10_MAX_NTA_LOOKUP_API}
            pageName='NTA_ICD10_MAX_NTA_LOOKUP'
          />
        </TabPane>
      </StyledTabs>
    </>
  );
};

export default NTAPage;
